import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbPlus, TbPencil, TbTrash, TbUsers, TbUser, TbLoader2, TbTemplate, TbCalendarPlus } from "react-icons/tb";
import { Section } from "./SettingsControls";
import { ConfirmPopup } from "src/components/Popup/ConfirmPopup";
import { GoaLineEditor } from "src/components/GoaLineEditor/GoaLineEditor";
import { activeRows, blankDraftRow, type GoaLineDraft } from "src/components/GoaLineEditor/goaLineDraft";
import { findDuplicateCodes } from "src/helpers/goaRules";
import { useGoaCatalog } from "src/hooks/useGoaCatalog";
import { useJustifications } from "src/hooks/useJustifications";
import { useInvoiceTemplates } from "src/hooks/useInvoiceTemplates";
import { apiRequest } from "src/services/apiClient";
import {
    templateCodesText,
    templateDayCount,
    templateLinesFromDays,
    templateRows,
    templateTotal,
    type InvoiceTemplate,
} from "src/helpers/invoiceTemplates";
import { checkServices } from "src/pages/InvoiceCreate/servicesRules";
import { templateError } from "src/pages/InvoiceCreate/TemplateDialogs";
import "./invoiceTemplateSettings.scss";

const euro = (n: number) =>
    n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** `days`: one row list per treatment day of the template. */
type Editing = { id: string | null; name: string; shared: boolean; mine: boolean; days: GoaLineDraft[][] };

/**
 * Settings → Rechnungsvorlagen (chapter 18, story 14): the service-line templates used in step 3 of
 * "Rechnung erstellen" — create, rename, change lines, share with the practice, delete. Shows the viewer's
 * own templates and the practice's shared ones; only the creator (or an admin, for shared ones) can change them.
 */
export function InvoiceTemplateSettings() {
    const { t } = useTranslation();
    const c = (key: string, opts?: Record<string, unknown>) => t(`invoiceTemplates.${key}`, opts);
    const { templates, loading, error: loadError } = useInvoiceTemplates();
    const [editing, setEditing] = useState<Editing | null>(null);
    const [deleting, setDeleting] = useState<InvoiceTemplate | null>(null);
    const [error, setError] = useState<string | null>(null);

    const startNew = () => {
        setError(null);
        setEditing({ id: null, name: "", shared: false, mine: true, days: [[blankDraftRow()]] });
    };
    const startEdit = (tpl: InvoiceTemplate) => {
        setError(null);
        const days = Array.from({ length: Math.max(1, templateDayCount(tpl)) }, (_, d) => templateRows(tpl, d));
        setEditing({ id: tpl.id, name: tpl.name, shared: tpl.shared, mine: tpl.mine, days });
    };

    const remove = async (tpl: InvoiceTemplate) => {
        setDeleting(null);
        try {
            await apiRequest(`/invoice-templates/${tpl.id}`, { method: "DELETE" });
            if (editing?.id === tpl.id) setEditing(null);
        } catch (e) {
            setError(templateError(e, t));
        }
    };

    return (
        <Section
            title={c("settingsTitle")}
            description={c("settingsDescription")}
            bodyClassName="template-settings"
            actions={!editing && (
                <button type="button" className="template-settings__new" onClick={startNew}>
                    <TbPlus aria-hidden /> {c("new")}
                </button>
            )}
        >
            {error && !editing && <p className="template-settings__error" role="alert">{error}</p>}

            {editing && (
                <TemplateEditor
                    key={editing.id ?? "new"}
                    initial={editing}
                    onDone={() => setEditing(null)}
                />
            )}

            {loading && templates.length === 0 ? (
                <p className="template-settings__empty" role="status"><TbLoader2 aria-hidden className="template-settings__spin" /> {c("loading")}</p>
            ) : loadError ? (
                <p className="template-settings__error" role="alert">{c("errors.load")}</p>
            ) : templates.length === 0 ? (
                !editing && (
                    <div className="template-settings__empty">
                        <TbTemplate aria-hidden className="template-settings__empty-icon" />
                        <p>{c("emptySettings")}</p>
                    </div>
                )
            ) : (
                <ul className="template-settings__list">
                    {templates.map((tpl) => (
                        <li key={tpl.id} className={`template-settings__item${editing?.id === tpl.id ? " template-settings__item--editing" : ""}`}>
                            <div className="template-settings__info">
                                <span className="template-settings__name">
                                    {tpl.name}
                                    <span className={`chip ${tpl.shared ? "chip--info" : "chip--neutral"}`}>
                                        {tpl.shared ? <TbUsers aria-hidden /> : <TbUser aria-hidden />}
                                        {tpl.shared ? c("shared") : c("personal")}
                                    </span>
                                </span>
                                <span className="template-settings__meta">
                                    {templateDayCount(tpl) > 1 && <>{c("dayCount", { count: templateDayCount(tpl) })} · </>}
                                    {c("lineCount", { count: tpl.lines.length })} · {templateCodesText(tpl, (n) => c("dayN", { n }))} · {euro(templateTotal(tpl))}
                                    {" · "}{c("usedCount", { count: tpl.usageCount })}
                                    {!tpl.mine && <> · {c("byColleague")}</>}
                                </span>
                            </div>
                            {tpl.canEdit && (
                                <div className="template-settings__actions">
                                    <button type="button" className="template-settings__icon-btn" onClick={() => startEdit(tpl)} aria-label={c("editNamed", { name: tpl.name })} disabled={!!editing}>
                                        <TbPencil aria-hidden />
                                    </button>
                                    <button type="button" className="template-settings__icon-btn template-settings__icon-btn--danger" onClick={() => setDeleting(tpl)} aria-label={c("deleteNamed", { name: tpl.name })} disabled={!!editing}>
                                        <TbTrash aria-hidden />
                                    </button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {deleting && (
                <ConfirmPopup
                    title={c("deleteTitle")}
                    message={c(deleting.shared ? "deleteSharedMessage" : "deleteMessage", { name: deleting.name })}
                    confirmLabel={c("delete")}
                    onConfirm={() => void remove(deleting)}
                    onCancel={() => setDeleting(null)}
                />
            )}
        </Section>
    );
}

function TemplateEditor({ initial, onDone }: { initial: Editing; onDone: () => void }) {
    const { t } = useTranslation();
    const c = (key: string, opts?: Record<string, unknown>) => t(`invoiceTemplates.${key}`, opts);
    const nameId = useId();
    const { catalogList, catalogByNr } = useGoaCatalog();
    const justifications = useJustifications();
    const [name, setName] = useState(initial.name);
    const [shared, setShared] = useState(initial.shared);
    const [days, setDays] = useState<GoaLineDraft[][]>(initial.days);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [tried, setTried] = useState(false);
    const setDayRows = (index: number, rows: GoaLineDraft[]) => setDays((cur) => cur.map((r, i) => (i === index ? rows : r)));

    // Same line rules as step 3 of the wizard (per day; an empty day is an error) — a template that inserts
    // broken lines helps nobody.
    const check = useMemo(
        () => checkServices(Object.fromEntries(days.map((rows, i) => [`day${i}`, rows])), days.map((_, i) => `day${i}`), catalogByNr),
        [days, catalogByNr],
    );
    const duplicateCodes = useMemo(() => days.map((rows) => findDuplicateCodes(activeRows(rows))), [days]);
    const rowIssues = useMemo(() => {
        if (!tried) return {};
        const out: Record<string, string[]> = {};
        for (const [id, problems] of Object.entries(check.problems)) out[id] = problems.map((p) => t(`invoiceCreate.services.problems.${p}`));
        return out;
    }, [check, tried, t]);

    const save = async () => {
        setTried(true);
        if (!name.trim() || !check.ok || saving) return;
        setSaving(true);
        setError(null);
        const body = JSON.stringify({ name: name.trim(), lines: templateLinesFromDays(days), ...(initial.mine ? { shared } : {}) });
        try {
            await apiRequest(
                initial.id ? `/invoice-templates/${initial.id}` : "/invoice-templates",
                { method: initial.id ? "PUT" : "POST", body },
                { "Content-Type": "application/json" },
            );
            onDone();
        } catch (e) {
            setError(templateError(e, t));
            setSaving(false);
        }
    };

    return (
        <form className="template-editor" onSubmit={(e) => { e.preventDefault(); void save(); }} aria-label={initial.id ? c("editTitle") : c("newTitle")}>
            <h3 className="template-editor__title">{initial.id ? c("editTitle") : c("newTitle")}</h3>
            <div className="template-editor__head">
                <label className="template-editor__field" htmlFor={nameId}>
                    <span>{c("name")}</span>
                    <input id={nameId} type="text" value={name} maxLength={80} placeholder={c("namePlaceholder")} autoFocus onChange={(e) => setName(e.target.value)} aria-invalid={tried && !name.trim()} />
                </label>
                {initial.mine && (
                    <label className="template-editor__check">
                        <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
                        <span>{c("shareWithPractice")}</span>
                    </label>
                )}
            </div>

            {days.map((rows, i) => (
                <section key={i} className="template-editor__day" aria-label={days.length > 1 ? c("dayN", { n: i + 1 }) : undefined}>
                    {days.length > 1 && (
                        <div className="template-editor__day-head">
                            <h4 className="template-editor__day-title">{c("dayN", { n: i + 1 })}</h4>
                            <button
                                type="button"
                                className="template-editor__day-remove"
                                onClick={() => setDays((cur) => cur.filter((_, j) => j !== i))}
                                aria-label={c("removeDay", { n: i + 1 })}
                            >
                                <TbTrash aria-hidden /> {c("removeDayShort")}
                            </button>
                        </div>
                    )}
                    {tried && check.emptyDays.includes(`day${i}`) && <p className="template-editor__error">{c("emptyDay")}</p>}
                    <GoaLineEditor
                        rows={rows}
                        onRowsChange={(next) => setDayRows(i, next)}
                        catalogList={catalogList}
                        catalogByNr={catalogByNr}
                        duplicateCodes={duplicateCodes[i]}
                        priceEditable
                        rowIssues={rowIssues}
                        initialExpandedId={!initial.id && i === 0 ? rows[0]?._localId ?? null : null}
                        reorderable
                        justifications={justifications}
                    />
                </section>
            ))}
            <button type="button" className="template-editor__add-day" onClick={() => setDays((cur) => [...cur, [blankDraftRow()]])}>
                <TbCalendarPlus aria-hidden /> {c("addDay")}
            </button>

            {tried && (!name.trim() || !check.ok) && (
                <p className="template-editor__error" role="alert">{!name.trim() ? c("errors.INVALID_NAME") : check.rowCount === 0 ? c("errors.INVALID_LINES") : c("fixLines")}</p>
            )}
            {error && <p className="template-editor__error" role="alert">{error}</p>}

            <div className="template-editor__footer">
                <span className="template-editor__total">{c("total")}: <strong>{euro(check.totals.total)}</strong></span>
                <button type="button" className="template-editor__cancel" onClick={onDone}>{t("popup.cancel")}</button>
                <button type="submit" className="template-editor__save" disabled={saving}>
                    {saving ? c("saving") : c("save")}
                </button>
            </div>
        </form>
    );
}
